import { useState } from "react";
import { Search, Calendar, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AppSidebar from "@/components/AppSidebar";
import { useAuth } from "@/contexts/AuthContext";

interface Briefing {
  id: string;
  festival_name: string;
  festival_date_start: string;
  status: "draft" | "published" | "archived";
  created_at: string;
}

const BriefingArchive = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { userId } = useAuth();

  // DEFENSE IN DEPTH: explicit producer_id filter
  const { data: archivedBriefings = [], isLoading } = useQuery({
    queryKey: ["archived-briefings", userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("briefings")
        .select("*")
        .eq("producer_id", userId)
        .eq("status", "archived")
        .order("festival_date_start", { ascending: false });

      if (error) throw error;
      return data as Briefing[];
    },
    enabled: !!userId,
  });

  const filteredBriefings = archivedBriefings.filter((briefing) =>
    briefing.festival_name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="flex min-h-screen">
      <AppSidebar />

      <main className="flex-1 bg-muted/20 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold">Briefing Archive</h1>
          <p className="text-muted-foreground">View past event briefings</p>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search archived briefings"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-border bg-card pl-10"
            />
          </div>
        </div>

        {/* Archived Briefings List */}
        <div>
          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">Loading...</div>
          ) : filteredBriefings.length === 0 ? (
            <Card className="bg-card p-12 text-center">
              <p className="text-muted-foreground">No archived briefings found.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredBriefings.map((briefing) => (
                <Card
                  key={briefing.id}
                  className="flex items-center gap-6 border-border bg-muted/30 p-4 transition-colors hover:bg-muted/40"
                >
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-muted-foreground">
                      {briefing.festival_name || "Untitled Briefing"}
                    </h3>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {briefing.festival_date_start
                        ? formatDate(briefing.festival_date_start)
                        : "No date"}
                    </span>
                  </div>

                  <Badge variant="secondary" className="bg-muted text-muted-foreground">
                    Archived
                  </Badge>

                  <Link to={`/briefings/${briefing.id}/review`}>
                    <Button variant="outline" size="sm">
                      <Eye className="mr-2 h-4 w-4" />
                      View Only
                    </Button>
                  </Link>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BriefingArchive;
